import { GraphQLClient } from 'graphql-request'
import { plainToInstance } from 'class-transformer'
import { DateTime } from 'luxon'
import { CampaignType } from '@models/promotionCampaign/CampaignType'
import { CampaignListResponse } from '@models/promotionCampaign/CampaignListResponse'
import { CampaignFormType } from '@models/promotionCampaign/CampaignFormType'
import { GET_CAMPAIGN_LIST } from './schemas/getCampaignList'
import { GET_CAMPAIGN_DETAIL } from './schemas/getCampaignDetail'
import { CREATE_CAMPAIGN } from './schemas/createCampaign'
import { UPDATE_CAMPAIGN } from './schemas/updateCampaign'
import { DELETE_CAMPAIGN } from './schemas/deleteCampaign'
import { APPROVE_CAMPAIGN } from './schemas/approveCampaign'
import { REJECT_CAMPAIGN } from './schemas/rejectCampaign'
import { UNAPPROVE_CAMPAIGN } from './schemas/unapproveCampaign'

function toStartOfDay(date?: string): string | undefined {
  if (!date) return undefined
  return DateTime.fromISO(date).startOf('day').toISO() || undefined
}

function toEndOfDay(date?: string): string | undefined {
  if (!date) return undefined
  return DateTime.fromISO(date).endOf('day').toISO() || undefined
}

export class PromotionCampaignClient {
  constructor(private client: GraphQLClient) {}

  async getCampaignList(params: {
    page: number
    limitPerPage: number
    searchText?: string
    type?: string
    status?: string
  }): Promise<CampaignListResponse> {
    const { campaignList } = await this.client.request(GET_CAMPAIGN_LIST, {
      page: params.page,
      limitPerPage: params.limitPerPage,
      searchText: params.searchText || undefined,
      type: params.type || undefined,
      status: params.status || undefined,
    })
    return plainToInstance(CampaignListResponse, campaignList)
  }

  async getCampaignDetail(id: number): Promise<CampaignType> {
    const { campaign } = await this.client.request(GET_CAMPAIGN_DETAIL, {
      id,
    })
    return plainToInstance(CampaignType, campaign)
  }

  async createCampaign(form: CampaignFormType): Promise<number> {
    const { createCampaign } = await this.client.request(CREATE_CAMPAIGN, {
      createCampaignInput: {
        name: form.name,
        type: form.type,
        status: form.status,
        wholeStoryDiscountType: form.wholeStoryDiscountType || undefined,
        freeReadingType: form.freeReadingType || undefined,
        items: form.bookData.map(item => ({
          bookId: item.bookId,
          bookTitle: item.bookTitle,
          bookPenFirstName: item.bookPenFirstName,
          bookPublisherName: item.bookPublisherName,
          chapterIdStart: item.chapterIdStart,
          chapterIdEnd: item.chapterIdEnd,
          discountPricePerChapter: item.discountPricePerChapter,
          discountPercentage: item.discountPercentage,
          discountCoin: item.discountCoin,
          originalPrice: item.originalPrice,
          discountedPrice: item.discountedPrice,
          dailyChapters: item.dailyChapters,
          freeDays: item.freeDays,
          startDate: toStartOfDay(item.startDate),
          endDate: toEndOfDay(item.endDate),
        })),
      },
    })
    return createCampaign.id
  }

  async updateCampaign(form: CampaignFormType & { id: number }): Promise<void> {
    await this.client.request(UPDATE_CAMPAIGN, {
      updateCampaignInput: {
        id: form.id,
        name: form.name,
        status: form.status,
        wholeStoryDiscountType: form.wholeStoryDiscountType || undefined,
        freeReadingType: form.freeReadingType || undefined,
        items: form.bookData.map(item => ({
          bookId: item.bookId,
          bookTitle: item.bookTitle,
          bookPenFirstName: item.bookPenFirstName,
          bookPublisherName: item.bookPublisherName,
          chapterIdStart: item.chapterIdStart,
          chapterIdEnd: item.chapterIdEnd,
          discountPricePerChapter: item.discountPricePerChapter,
          discountPercentage: item.discountPercentage,
          discountCoin: item.discountCoin,
          originalPrice: item.originalPrice,
          discountedPrice: item.discountedPrice,
          dailyChapters: item.dailyChapters,
          freeDays: item.freeDays,
          startDate: toStartOfDay(item.startDate),
          endDate: toEndOfDay(item.endDate),
        })),
      },
    })
  }

  async deleteCampaign(id: number): Promise<void> {
    await this.client.request(DELETE_CAMPAIGN, { id })
  }

  async approveCampaign(id: number): Promise<void> {
    await this.client.request(APPROVE_CAMPAIGN, { id })
  }

  async unapproveCampaign(id: number): Promise<void> {
    await this.client.request(UNAPPROVE_CAMPAIGN, { id })
  }

  async rejectCampaign(id: number): Promise<void> {
    await this.client.request(REJECT_CAMPAIGN, { id })
  }
}
